import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** Registro de avance de obra: descripción, % estimado y fotos. */
export const ProgressDialog = ({ open, onOpenChange, projectId }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [descripcion, setDescripcion] = useState("");
  const [avancePct, setAvancePct] = useState("");
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    setDescripcion(""); setAvancePct(""); setFiles([]);
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append("descripcion", descripcion);
      form.append("avance_pct", String(avancePct));
      files.forEach((f) => form.append("files", f));
      return api.post(`/projects/${projectId}/progress`, form);
    },
    onSuccess: () => {
      toast.success(t("app.saved"));
      qc.invalidateQueries({ queryKey: ["project-detail", projectId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const pct = Number(avancePct);
  const blocked = !descripcion.trim() || Number.isNaN(pct) || pct < 0 || pct > 100 || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="progress-dialog" className="max-w-md rounded-[10px]">
        <DialogHeader>
          <DialogTitle>{t("projects.progress.add")}</DialogTitle>
        </DialogHeader>

        <div>
          <Label className="text-[12.5px]">{t("projects.progress.description")} *</Label>
          <Textarea data-testid="progress-description-input" rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        <div>
          <Label className="text-[12.5px]">{t("projects.progress.percent")} *</Label>
          <Input data-testid="progress-percent-input" type="number" min={0} max={100} value={avancePct}
            onChange={(e) => setAvancePct(e.target.value)} />
        </div>
        <div>
          <Label className="text-[12.5px]">{t("projects.progress.photos")}</Label>
          <label className="mt-1 flex h-9 cursor-pointer items-center gap-2 border border-line px-3 text-[12.5px] text-ink-secondary hover:border-line-strong">
            <Camera className="h-3.5 w-3.5" />
            {files.length ? `${files.length} foto(s)` : t("projects.progress.photos")}
            <input type="file" accept="image/*,.heic,.heif" multiple data-testid="progress-photos-input" className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </label>
        </div>

        <DialogFooter>
          <Button data-testid="progress-submit-button" disabled={blocked} onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("app.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
